import { useMutation, useQuery, useQueryClient } from "react-query";
import RolesTable from "./roles_table";
import {
  createRole,
  deleteRole,
  editRole,
  fetchPermissions,
  fetchRoles,
} from "../../../api/setting";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaTimes } from "react-icons/fa";
import { buildPermissionLabel } from "../../../utils/utils";
import { Checkbox, Modal } from "antd";
import Loading from "../../shared/Loading";
import { usePermissionEffect } from "../../../utils/usePermissionEffect";

export default function Roles() {
  const [showModal, setShowModal] = useState(false);
  const { data } = useQuery(["roles"], fetchRoles);
  return (
    <div className="flex flex-col gap-7 font-Neurial w-full">
      {showModal && (
        <CreateRoleModal
          open={showModal}
          handleClose={() => setShowModal(false)}
        />
      )}
      <div className="flex justify-between">
        <h4 className="font-bold text-lg">Roles List</h4>
        <button
          className="bg-[#0000000D] p-2 rounded-[5px]"
          onClick={() => setShowModal(true)}
        >
          Create Role
        </button>
      </div>
      <div className="flex gap-4 w-full overflow-x-auto whitespace-nowrap">
        {(data?.roles || [])?.map((role) => (
          <RoleList role={role} />
        ))}
      </div>
      <RolesTable />
    </div>
  );
}

const RoleList = ({ role }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { getModulePermission } = usePermissionEffect();
  return (
    <div className="w-[360px] relative">
      {showModal && (
        <EditRoleModal
          open={showModal}
          handleClose={() => setShowModal(false)}
          data={role}
        />
      )}
      {showDeleteModal && (
        <DeleteRoleModal
          open={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          data={role}
        />
      )}
      <div className="bg-white rounded-lg p-4 flex items-center justify-between w-full gap-5">
        <div className="flex flex-col gap-3">
          <div className="text-[#828282] text-sm">
            Total {role?.numberOfUserAssigned || 0} users
          </div>
          <div className="font-bold text-lg">{role?.title}</div>
          <div className="flex gap-4">
            <button
              className="text-[#2121F1] text-sm w-fit"
              onClick={() => setShowModal(true)}
            >
              Edit role
            </button>
            {getModulePermission("role", "canDelete") && (
              <button
                className="text-[#E53935] text-sm w-fit"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete role
              </button>
            )}
          </div>
        </div>
        <div className="absolute right-[5px] flex">
          {Array.from({
            length:
              role.numberOfUserAssigned > 4 ? 4 : role.numberOfUserAssigned,
          })
            .fill(9)
            .map((ar) => (
              <div className="relative w-[33px] h-[33px] bg-black rounded-full border-white border-[1px]"></div>
            ))}
        </div>
      </div>
    </div>
  );
};

// The server refuses while any admin on the role is still active.
const DeleteRoleModal = ({ handleClose, open, data: roleData }) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(deleteRole, {
    onSuccess: () => {
      queryClient.invalidateQueries("roles");
      toast.success("Role deleted");
      handleClose();
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });

  return (
    <Modal
      title={`Delete ${roleData?.title || "role"}?`}
      open={open}
      onCancel={() => !mutation.isLoading && handleClose()}
      okText="Delete"
      okButtonProps={{ danger: true, loading: mutation.isLoading }}
      onOk={() => mutation.mutate(roleData._id)}
    >
      <p className="text-[13px]">
        This can't be undone. Every admin assigned to this role must be
        deactivated before it can be deleted.
      </p>
    </Modal>
  );
};

const CreateRoleModal = ({ handleClose, open }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [permissions, setPermission] = useState([]);

  const handleCheckChange = (key, value, index) => {
    setPermission((prev) => {
      // Copy the entire array
      const updatedPermissions = [...prev];
      const item = { ...updatedPermissions[index] };
      item.permissions = { ...item.permissions, [key]: value };

      updatedPermissions[index] = item;

      return updatedPermissions;
    });
  };

  const { data, isLoading } = useQuery("permissions", fetchPermissions);

  useEffect(() => {
    if (data?.permissionFeatures && !permissions.length) {
      setPermission(data?.permissionFeatures);
    }
  }, [data]);

  const mutation = useMutation(createRole, {
    onSuccess: () => {
      queryClient.invalidateQueries("roles");
      toast.success("Role added");
      handleClose();
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });

  const onFinish = (e) => {
    e.preventDefault();

    const values = {
      title,
      permissions,
    };

    mutation.mutate(values);
  };

  return (
    <Modal
      title="Create Role"
      open={open}
      footer={null}
      onCancel={() => handleClose()}
    >
      <div className="space-y-5 px-7">
        <div className="w-full h-full py-5 px-5">
          <div className="w-full h-[450px] overflow-y-scroll p-2">
            <div className="space-y-3 mb-5">
              <div className="text-[#181819E5] text-[15px]">Title</div>
              <input
                type="text"
                className="w-full text-[14px] placeholder:text-[#A0A3BD] border-[#E6E7E9] border-[1px] bg-transparent rounded-lg h-[45px] px-4"
                placeholder="Role title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-[24px] overflow-y-scroll h-[calc(100vh-270px)] ">
              <h3 className="text-[18px] font-[500] mb-[5px]">Permissions</h3>

              {isLoading ? (
                <Loading />
              ) : (
                <>
                  {permissions.map((item, index) => {
                    const arr = Object.entries(item.permissions);

                    return (
                      <div className="flex flex-col gap-[24px]" key={index}>
                        <p className="text-[16px] font-[500] capitalize">
                          {item.module}
                        </p>
                        <div className="space-y-[24px]">
                          {arr.map((item2, k) => (
                            <Checkbox
                              checked={item2[1]}
                              onChange={(e) =>
                                handleCheckChange(item2[0], !item2[1], index)
                              }
                              key={k}
                            >
                              {buildPermissionLabel(item2[0], item.module)}
                            </Checkbox>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
            <div className="flex items-center w-full font-bold text-[14px] gap-5 py-10">
              <button
                type="button"
                onClick={onFinish}
                className={`w-full bg-[#1B0A4B] p-2 rounded-[5px] text-white ${
                  mutation.isLoading ? "animate-pulse" : ""
                }`}
              >
                {mutation.isLoading ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className="w-full bg-[#0000000D] p-2 rounded-[5px]"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const EditRoleModal = ({ handleClose, open, data: roleData }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [permissions, setPermission] = useState([]);

  const handleCheckChange = (key, value, index) => {
    setPermission((prev) => {
      // Copy the entire array
      const updatedPermissions = [...prev];
      const item = { ...updatedPermissions[index] };
      item.permissions = { ...item.permissions, [key]: value };

      updatedPermissions[index] = item;

      return updatedPermissions;
    });
  };

  const { data: features } = useQuery("permissions", fetchPermissions);

  // show every module, including ones added after this role was created
  // (e.g. Monitoring), so they can be granted
  useEffect(() => {
    if (roleData?.permissions && features && !permissions.length) {
      const existing = roleData.permissions;
      const missing = (features?.permissionFeatures || []).filter(
        (f) => !existing.some((p) => p.module?.toLowerCase() === f.module.toLowerCase()),
      );
      setPermission([
        ...existing.map((p) => {
          const f = features?.permissionFeatures?.find((x) => x.module.toLowerCase() === p.module?.toLowerCase());
          return f ? { ...p, permissions: { ...f.permissions, ...p.permissions } } : p;
        }),
        ...missing,
      ]);
    }
    if (roleData?.title) {
      setTitle(roleData?.title);
    }
  }, [roleData, features]);

  const mutation = useMutation(editRole, {
    onSuccess: () => {
      queryClient.invalidateQueries("roles");
      toast.success("Role added");
      handleClose();
    },
    onError: (e) => {
      toast.error(e.message);
    },
  });

  const onFinish = (e) => {
    e.preventDefault();

    const values = {
      id: roleData._id,
      title,
      permissions,
    };

    mutation.mutate(values);
  };

  return (
    <Modal
      title="Edit Role"
      open={open}
      footer={null}
      onCancel={() => handleClose()}
    >
      <div className="space-y-5 px-7">
        <div className="w-full h-full py-5 px-5">
          <div className="w-full h-[450px] overflow-y-scroll p-2">
            <div className="space-y-3 mb-5">
              <div className="text-[#181819E5] text-[15px]">Title</div>
              <input
                type="text"
                className="w-full text-[14px] placeholder:text-[#A0A3BD] border-[#E6E7E9] border-[1px] bg-transparent rounded-lg h-[45px] px-4"
                placeholder="Role title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-[24px] overflow-y-scroll h-[calc(100vh-270px)] ">
              <h3 className="text-[18px] font-[500] mb-[5px]">Permissions</h3>

              <>
                {permissions.map((item, index) => {
                  const arr = Object.entries(item.permissions);

                  return (
                    <div className="flex flex-col gap-[24px]" key={index}>
                      <p className="text-[16px] font-[500] capitalize">
                        {item.module}
                      </p>
                      <div className="space-y-[24px]">
                        {arr.map((item2, k) => (
                          <Checkbox
                            checked={item2[1]}
                            onChange={(e) =>
                              handleCheckChange(item2[0], !item2[1], index)
                            }
                            key={k}
                          >
                            {buildPermissionLabel(item2[0], item.module)}
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            </div>
            <div className="flex items-center w-full font-bold text-[14px] gap-5 py-10">
              <button
                type="button"
                onClick={onFinish}
                className={`w-full bg-[#1B0A4B] p-2 rounded-[5px] text-white ${
                  mutation.isLoading ? "animate-pulse" : ""
                }`}
              >
                {mutation.isLoading ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                className="w-full bg-[#0000000D] p-2 rounded-[5px]"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
